import React, { useEffect, useState } from "react";
import Pencil from "../../../../images/pencil.png";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setBasicInfo } from "../../../../features/candidate/personalSlice";
import { mapToOption, formatPhone, formatDateCST, formatTimeCST } from "../../../../utlis";
import { useCandidateForm } from "../../CandidateFormContext";
import { toast } from "react-toastify";
import useAddressValidation from "../../../../hooks/useAddressValidation";
import AddressValidationDialog from "../../../../components/AddressValidationDialog";

const Basic = () => {
  const {
    firstName,
    lastName,
    middleName,
    gender,
    maritalStatus,
    dob,
    email,
    address1,
    address2,
    phone,
    alternatePhoneNum,
    city,
    state,
    zip,
    status
  } = useSelector((state) => state.personal.basic);

  const candidateId = useSelector((state) => state.personal.candidateId);
  const audit = useSelector((state) => state.personal.audit);

  const [basicData, setBasicData] = useState({
    firstName,
    lastName,
    middleName,
    gender,
    maritalStatus,
    dob,
    email,
    address1,
    address2,
    phone,
    alternatePhoneNum,
    city,
    state,
    zip,
    status
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isCreating, markTabComplete, hasUnsavedChanges, isSaving, saveAllChanges } = useCandidateForm();
  const {
    validateAndProceed, isValidating, dialogState,
    acceptSuggestion, keepOriginal, saveAnyway, cancelDialog,
  } = useAddressValidation({
    fieldMap: { street: 'address1', street2: 'address2' },
  });

  // Validate required fields
  const validateBasicInfo = () => {
    const requiredFields = [
      { field: 'firstName', label: 'First Name' },
      { field: 'lastName', label: 'Last Name' },
      { field: 'gender', label: 'Gender' },
      { field: 'maritalStatus', label: 'Marital Status' },
      { field: 'dob', label: 'Date of Birth' },
      { field: 'email', label: 'Email' },
      { field: 'phone', label: 'Phone Number' },
      { field: 'address1', label: 'Address Line 1' },
      { field: 'city', label: 'City' },
      { field: 'state', label: 'State' },
      { field: 'zip', label: 'Zip Code' },
      { field: 'status', label: 'Status' },
    ];

    for (const { field, label} of requiredFields) {
      if (!basicData[field] || basicData[field].toString().trim() === '') {
        toast.error(`${label} is required`);
        return false;
      }
    }

    // Validate phone number length (should be 10 digits)
    if (basicData.phone && basicData.phone.replace(/\D/g, '').length !== 10) {
      toast.error('Phone Number must be 10 digits');
      return false;
    }

    return true;
  };

  // Handle Next button click
  const handleNext = () => {
    // In create mode, validate before proceeding
    if (isCreating) {
      if (!validateBasicInfo()) {
        return;
      }
    }
    // Validate address, then proceed
    validateAndProceed(basicData, (validatedData) => {
      // Block save if city or state is still empty after validation
      if (!validatedData.city?.trim() || !validatedData.state?.trim()) {
        toast.error('City and State are required. Please validate your address or enter them manually.');
        return;
      }
      setBasicData(validatedData);
      dispatch(setBasicInfo(validatedData));
      if (isCreating) {
        markTabComplete('basic');
      }
      navigate('/candidate/add-candidate/personal/professional');
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;

    // Format phone numbers
    if (name === 'phone' || name === 'alternatePhoneNum') {
      // Remove all non-digit characters first
      const digits = value.replace(/\D/g, '');
      // Limit to 10 digits
      formattedValue = digits.slice(0, 10);
    }

    setBasicData((pervData) => ({
      ...pervData,
      [name]: formattedValue,
    }));
  };


  const referenceData = useSelector((state) => state.reference);

  useEffect(() => {
    dispatch(setBasicInfo(basicData));
    localStorage.setItem("state", JSON.stringify(basicData));
  }, [basicData]);

  // Format DOB to YYYY-MM-DD for date input
  const formatDOBForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="">
      <from className="flex flex-col gap-y-6 mt-5 text-sm font-normal text-text-hint">
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              First Name<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={firstName}
                name="firstName"
                placeholder="First Name"
                maxLength={25}
                required
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              Last Name<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={lastName}
                name="lastName"
                maxLength={25}
                placeholder="Last Name"
                required
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              Middle Name
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={middleName}
                name="middleName"
                maxLength={25}
                placeholder="Middle Name"
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 ">
          <div className="">
            <label>
              Gender<span className="text-red-500">*</span>
            </label>
            <ul className="grid grid-cols-3 mt-1">
              <li className="relative">
                <input
                  onChange={handleChange}
                  value="male"
                  checked={basicData.gender === "male"}
                  className="sr-only peer"
                  type="radio"
                  name="gender"
                  id="male"
                />
                <label
                  className="flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 rounded-tl-full rounded-bl-full cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800"
                  htmlFor="male"
                >
                  Male
                </label>
              </li>
              <li className="relative">
                <input
                  onChange={handleChange}
                  value="female"
                  checked={basicData.gender === "female"}
                  className="sr-only peer"
                  type="radio"
                  name="gender"
                  id="female"
                />
                <label
                  className="flex justify-center items-center py-2 px-3 bg-white border border-t-secondary-800 border-b-secondary-800 cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800"
                  htmlFor="female"
                >
                  Female
                </label>
              </li>

              <li className="relative">
                <input
                  onChange={handleChange}
                  value="other"
                  checked={basicData.gender === "other"}
                  className="sr-only peer"
                  type="radio"
                  name="gender"
                  id="other"
                />
                <label
                  className="flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 rounded-tr-full rounded-br-full cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800"
                  htmlFor="other"
                >
                  Other
                </label>
              </li>
            </ul>
          </div>
          <div className="">
            <label>
              Marital Status<span className="text-red-500">*</span>
            </label>
            <ul className="grid grid-cols-3 mt-1">
              <li className="relative">
                <input
                  onChange={handleChange}
                  value="single"
                  checked={basicData.maritalStatus === "single"}
                  className="sr-only peer"
                  type="radio"
                  name="maritalStatus"
                  id="single"
                />
                <label
                  className="flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 rounded-tl-full rounded-bl-full cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800"
                  htmlFor="single"
                >
                  Single
                </label>
              </li>

              <li className="relative">
                <input
                  onChange={handleChange}
                  value="married"
                  checked={basicData.maritalStatus === "married"}
                  className="sr-only peer"
                  type="radio"
                  name="maritalStatus"
                  id="married"
                />
                <label
                  className="flex justify-center items-center py-2 px-3 bg-white border border-secondary-800 rounded-tr-full rounded-br-full cursor-pointer focus:outline-none hover:bg-gray-50 peer-checked:bg-secondary-500 peer-checked:text-white peer-checked:border-secondary-800"
                  htmlFor="married"
                >
                  Married
                </label>
              </li>
            </ul>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="max-w-xs">
            <label className="">
              DOB<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={formatDOBForInput(dob)}
                name="dob"
                type="date"
                required
                className="outline-none border-none w-full text-black bg-transparent"
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="">
              Email<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={email}
                name="email"
                placeholder="youremail@gmail.com"
                maxLength={50}
                required
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              Phone Number<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={formatPhone(phone)}
                name="phone"
                placeholder="(999) 999 9999"
                required
                maxLength={15}
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              Alternative Phone Number
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={formatPhone(alternatePhoneNum)}
                name="alternatePhoneNum"
                placeholder="(999) 999 9999"
                maxLength={15}
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              Address Line 1<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={address1}
                name="address1"
                placeholder="Address Line 1"
                required
                maxLength={50}
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              Address Line 2<span className="text-red-500"></span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={address2}
                name="address2"
                placeholder="Address Line 2"
                required
                maxLength={50}
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              City
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={basicData.city || ''}
                name="city"
                placeholder="City"
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              State
            </label>
            <select
              onChange={handleChange}
              name="state"
              value={basicData.state || ''}
              className="flex justify-center items-center text-black focus:ring-0 px-3 py-2 mt-1 border border-neutral-500 rounded-lg outline-none w-full"
            >
              <option value="">-- Select State --</option>
              {mapToOption(referenceData.states?.state, 'name').map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">
              Zip<span className="text-red-500">*</span>
            </label>
            <div className="flex justify-center items-center px-3 py-2 mt-1 border border-neutral-500 rounded-lg h-10">
              <input
                onChange={handleChange}
                value={zip}
                name="zip"
                placeholder="zip"
                required
                maxLength={10}
                className="outline-none w-full text-black"
              />
              <img src={Pencil} alt="pencil" />
            </div>
          </div>
          <div className="">
            <label className="">
              Status<span className="text-red-500">*</span>
            </label>
            <select
              onChange={handleChange}
              name="status"
              value={status}
              className="flex justify-center items-center text-black focus:ring-0 px-3 py-2 mt-1 border border-neutral-500 rounded-lg outline-none w-full"
            >
              {mapToOption(referenceData.candidatestatuses?.candidate, 'status').map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>
        </div>
      </from>
      {/* Audit Fields - Only show in Edit Mode */}
      {candidateId && !isCreating && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Created By</label>
            <p className="text-sm text-gray-600">{audit?.createdBy || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Date Created</label>
            <p className="text-sm text-gray-600">
              {audit?.dateCreated ? formatDateCST(audit.dateCreated) : 'N/A'}
            </p>
            {audit?.dateCreated && (
              <p className="text-xs text-gray-500">{formatTimeCST(audit.dateCreated)}</p>
            )}
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Updated By</label>
            <p className="text-sm text-gray-600">{audit?.updatedBy || 'N/A'}</p>
          </div>
          <div className="flex flex-col space-y-1">
            <label className="text-text-hint text-xs">Last Updated</label>
            <p className="text-sm text-gray-600">
              {audit?.lastUpdated ? formatDateCST(audit.lastUpdated) : 'N/A'}
            </p>
            {audit?.lastUpdated && (
              <p className="text-xs text-gray-500">{formatTimeCST(audit.lastUpdated)}</p>
            )}
          </div>
        </div>
      )}
      <div className="w-full py-10 px-8 flex justify-end gap-5">
        {!isCreating && hasUnsavedChanges && (
          <button
            disabled={isSaving}
            onClick={() => {
              validateAndProceed(basicData, async (validatedData) => {
                if (!validatedData.city?.trim() || !validatedData.state?.trim()) {
                  toast.error('City and State are required. Please validate your address or enter them manually.');
                  return;
                }
                setBasicData(validatedData);
                dispatch(setBasicInfo(validatedData));
                try {
                  await saveAllChanges();
                } catch (e) {
                  // error toast handled by context
                }
              });
            }}
            className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        )}
        <button
          onClick={handleNext}
          className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base hover:bg-secondary-800 transition"
        >
          Next
        </button>
      </div>

      <AddressValidationDialog
        dialogState={dialogState}
        onAcceptSuggestion={acceptSuggestion}
        onKeepOriginal={keepOriginal}
        onSaveAnyway={saveAnyway}
        onCancel={cancelDialog}
      />
    </div>
  );
};

export default Basic;
